import { NodeProps } from "@xyflow/react";
import { useMemo } from "react";
import { observer, Observer } from "mobx-react-lite";

import { useNode } from "../../node-context";

import NodeBody from "./NodeBody";
import NodeFooter from "./NodeFooter";
import NodeHeader from "./NodeHeader";
import { getActiveStyle, getBackgroundColor, getIconColor } from "./utils";

import { useWorkflow } from "@/components/Workflows/WorkflowCanvas/workflow-context";

export interface BasicNodeProps extends NodeProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  isConnectable: boolean;
  preview?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

const COLLAPSED_NODE_WIDTH = 400;
const EXPANDED_NODE_WIDTH = 640;

export const BasicNode = observer(
  ({ data, isConnectable, selectable, preview, style, id, className }: BasicNodeProps) => {
    const workflow = useWorkflow();

    return (
      <Observer>
        {() => {
          const node = useNode();
          const isSelected = selectable && workflow.isNodeSelected(id),
            isOutput = node?.is_output_node;
          const nodeErrors = node?.errors || [],
            hasErrors = nodeErrors.length > 0;
          const hasProviderApiKeyPrompt = Boolean(node?.providerApiKeyPrompt);
          const showHandles = !preview;
          let nodeWidth = COLLAPSED_NODE_WIDTH;

          if (!preview && hasProviderApiKeyPrompt) {
            nodeWidth = EXPANDED_NODE_WIDTH;
          }

          const baseStyle = useMemo(() => {
            const backgroundColor = getBackgroundColor(preview, hasErrors),
              activeStyle = getActiveStyle();
            return isSelected ? activeStyle : className || backgroundColor;
          }, [isSelected, hasErrors]);

          const { background: iconBackgroundColor, text: iconTextColor } = getIconColor(baseStyle);

          return (
            <div
              className={`border ${baseStyle} relative cursor-pointer rounded-md transition-[width] duration-300 ease-in-out ${
                preview ? "py-9" : "py-3"
              } shadow-md`}
              style={{
                ...style,
                width: nodeWidth,
              }}
              tabIndex={0}
            >
              {!preview && (
                <NodeHeader
                  type={data?.type}
                  isOutput={isOutput}
                  nodeStatus={node?.status}
                  iconBackgroundColor={iconBackgroundColor}
                  iconTextColor={iconTextColor}
                />
              )}
              <NodeBody
                isConnectable={isConnectable}
                showHandles={showHandles}
                label={data.label}
              />
              <NodeFooter />
            </div>
          );
        }}
      </Observer>
    );
  },
);

export default BasicNode;
